import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ProductReference } from '@epicuro-next/models/engagement';
import { isListUrl, isLookbookUrl } from '@epicuro-next/navigation/routes';
import {
  configStripConfigurationDefaultValues,
  ConfigStripConfigurationKeys,
  ConfigStripConfigurations,
  CONFIG_STRIP_FEATURE,
  selectConfigurationValueFrom,
} from '@epicuro-next/platform/configuration';
import { selectCollapse } from '@epicuro-next/state/p-basket-state';
import {
  patchActiveProductList,
  selectActiveListProductsReference,
  selectMarkVariantInCsForOpenedList,
  selectOpenedListProductReferences,
} from '@epicuro-next/state/product-list-state';
import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { Product } from 'graphql-api/gql-api-interfaces';
import { iif, Observable, of } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

interface State {
  product: Product | null;
  isBookmarking: boolean;
}

export const initialState = {
  product: null,
  isBookmarking: false,
};

@Injectable()
export class ProductListActionsBaseComponentStore extends ComponentStore<State> {
  constructor(protected readonly store: Store, public router: Router) {
    super(initialState);
  }
  public readonly product$ = this.select((state) => state.product);
  public readonly isBookmarking$ = this.select((state) => state.isBookmarking);

  public readonly openedListProductReferences$ = this.store.pipe(
    select(selectOpenedListProductReferences),
    map((productReferences) =>
      productReferences.reduce((acc, { productId }) => {
        acc[productId] = true;
        return acc;
      }, {} as { [key: string]: boolean }),
    ),
  );

  public groupByVariantEnabled$ = this.store.pipe(
    select(selectCollapse),
    takeUntil(this.destroy$),
  );

  public markVariantInOpenedList$ = this.store.pipe(
    select(selectMarkVariantInCsForOpenedList),
  );

  public showSizeGridBookmarkButton$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        ConfigStripConfigurationKeys.ShowSizeGridBookmarkButton,
        ConfigStripConfigurations
      >(
        CONFIG_STRIP_FEATURE,
        ConfigStripConfigurationKeys.ShowSizeGridBookmarkButton,
        configStripConfigurationDefaultValues[
          ConfigStripConfigurationKeys.ShowSizeGridBookmarkButton
        ],
      ),
    ),
  );

  public setProduct = this.effect((product$: Observable<Product>) => {
    return product$.pipe(
      tap((product: Product) => {
        this.patchState({ product });
      }),
    );
  });

  public isListPage() {
    return isListUrl(this.router.url) || isLookbookUrl(this.router.url);
  }

  public addedToActiveList(material?: Product) {
    return this.select(
      material ? of(material) : this.product$,
      this.store.pipe(select(selectActiveListProductsReference)),
      this.groupByVariantEnabled$,
      (
        product: Product,
        references: ProductReference[],
        groupByVariantEnabled: boolean,
      ) => {
        if (!references || !product) {
          return false;
        }

        return this.productOrRelatedAddedToList(
          product,
          references,
          groupByVariantEnabled,
        );
      },
    );
  }

  public notAddedToActiveList(material?: Product) {
    return this.addedToActiveList(material).pipe(
      map((isAddedToActiveList) => !isAddedToActiveList),
    );
  }

  public patchActiveProductList(material?: Product) {
    if (material) {
      this.setProduct(material);
    }
    this.product$
      .pipe(
        take(1),
        filter(Boolean),
        concatLatestFrom(() => [
          this.store.pipe(select(selectActiveListProductsReference)),
          this.groupByVariantEnabled$,
        ]),
        switchMap(([product, references, groupByVariantEnabled]) => {
          return iif(
            () =>
              this.productOrRelatedAddedToList(
                product,
                references ?? [],
                groupByVariantEnabled ?? false,
              ),
            of(product).pipe(
              map((item) =>
                this.firstProductOrRelatedAddedToList(item, references ?? []),
              ),
            ),
            of(product),
          );
        }),
        filter(Boolean),
      )
      .subscribe((product) => {
        this.store.dispatch(
          patchActiveProductList({ product: product as Product }),
        );
      });
  }

  public isMaterialInOpenedList(
    productId: string,
    openedListProductReferences: { [key: string]: boolean },
  ) {
    return openedListProductReferences[productId];
  }

  private productOrRelatedAddedToList(
    product: Product,
    references: ProductReference[],
    productsGrouped: boolean,
  ) {
    if (!references?.length) {
      return false;
    }

    const result = references.some(
      (item) =>
        item.brandId === product.brandId &&
        item.productId === product.productId,
    );

    if (!result && productsGrouped) {
      return (
        product.relatedVariants?.some((relatedVariant) =>
          references.some(
            (item) => item.productId === relatedVariant.productId,
          ),
        ) ?? false
      );
    }

    return result;
  }

  private firstProductOrRelatedAddedToList(
    product: Product,
    references: ProductReference[],
  ) {
    let result = references.find(
      (item) =>
        item.brandId === product.brandId &&
        item.productId === product.productId,
    );

    if (result) {
      return product;
    }

    result = product.relatedVariants?.find((relatedVariant) =>
      references.find((item) => item.productId === relatedVariant.productId),
    );

    if (!result) {
      return null;
    }

    return {
      ...result,
      brandId: result.brandId ?? product.brandId,
    };
  }
}
